/**
 * Photos asked for at the size they are drawn.
 *
 * Seeded listings and the curated pages hotlink stock photos, and a Pexels
 * address with no parameters is the photographer's original: on a phone the
 * home page downloaded three of them (2400 to 6720 px wide, 3.5 MB together)
 * to draw them 166 px wide, plus a 1.9 MB Unsplash original. Both CDNs
 * resize, compress and choose the format on request, so a card asks for its
 * own width and twice that for sharp screens.
 *
 * Any other address is returned exactly as given: our own partner media is
 * served as uploaded (the API does not resize), and a parameter it does not
 * know would at best be ignored.
 */

// Wider than any slot on the site at 2x; a cap so a mistake costs a large
// file, never a photographer's original.
const MAX_WIDTH = 2400;

// What each CDN needs besides the width. A crop or quality already chosen
// for that photo is kept; the width never is.
const CDNS = {
  // Compressed (WebP where the browser takes it), trimmed to sRGB.
  "images.pexels.com": (params) => {
    if (!(params.get("auto") || "").includes("compress")) params.set("auto", "compress");
    params.set("cs", "tinysrgb");
  },
  // imgix: WebP or AVIF where the browser takes it.
  "images.unsplash.com": (params) => {
    if (!(params.get("auto") || "").includes("format")) params.set("auto", "format");
    if (!params.has("fit")) params.set("fit", "crop");
    if (!params.has("q")) params.set("q", "70");
  },
};

function resizable(url) {
  if (typeof url !== "string") return null;
  let parsed;
  try { parsed = new URL(url); } catch { return null; }
  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return null;
  const configure = CDNS[parsed.hostname];
  return configure ? { parsed, configure } : null;
}

const clampWidth = (width) => Math.min(Math.max(Math.round(width), 1), MAX_WIDTH);

/** The address of `url` at `width` pixels wide, or `url` itself off a known CDN. */
export function sizedImage(url, width) {
  const cdn = resizable(url);
  if (!cdn || !(width > 0)) return url;
  const { parsed, configure } = cdn;
  const params = parsed.searchParams;
  const w = clampWidth(width);
  // A height picked with the old width keeps the crop's proportions; one
  // without a width would fight the new one.
  const oldWidth = Number(params.get("w"));
  const height = Number(params.get("h"));
  if (height > 0 && oldWidth > 0) params.set("h", String(Math.round((height * w) / oldWidth)));
  else params.delete("h");
  // The srcset carries the pixel density; a dpr here would multiply it.
  params.delete("dpr");
  configure(params);
  params.set("w", String(w));
  return parsed.toString();
}

/**
 * The src, srcSet and sizes of an <img> drawn about `width` CSS pixels wide
 * (`sizes` says how wide it really is at each breakpoint, the browser picks
 * the candidate). Off a known CDN, only the src, unchanged.
 *
 * `width` may instead list the candidates' widths, where the width and twice
 * it leave a gap: a photo drawn 750 px wide on a desktop and 500 px on a
 * phone at 2x is better served 1000 px wide there than 1500. The smallest is
 * the src.
 */
export function responsiveImage(url, width, sizes) {
  const widths = Array.isArray(width) ? width : [width, width * 2];
  if (!resizable(url) || !widths.length || !widths.every((w) => w > 0)) return { src: url };
  const candidates = [...new Set(widths.map(clampWidth))].sort((a, b) => a - b);
  const src = sizedImage(url, candidates[0]);
  const srcSet = candidates.map((w) => `${sizedImage(url, w)} ${w}w`).join(", ");
  return { src, srcSet, sizes: sizes || `${candidates[0]}px` };
}
